import { useCallback, useEffect, useState } from 'react';

export const COMMUNITY_TABS = [
  'overview',
  'models',
  'mtos',
  'suppliers',
  'labor',
  'audit',
  'bid',
] as const;

export type CommunityTab = (typeof COMMUNITY_TABS)[number];

const STORAGE_KEY = 'community-tab';

function isCommunityTab(value: string | null | undefined): value is CommunityTab {
  return COMMUNITY_TABS.includes(value as CommunityTab);
}

function readStoredTab(communityId: string | undefined): CommunityTab {
  if (!communityId) return 'overview';
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return 'overview';
    const map = JSON.parse(raw) as Record<string, string>;
    return isCommunityTab(map[communityId]) ? map[communityId] : 'overview';
  } catch {
    return 'overview';
  }
}

function writeStoredTab(communityId: string, tab: CommunityTab) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    map[communityId] = tab;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore quota / parse errors
  }
}

export function useCommunityTab(communityId: string | undefined) {
  const [tab, setTabState] = useState<CommunityTab>(() => readStoredTab(communityId));

  useEffect(() => {
    setTabState(readStoredTab(communityId));
  }, [communityId]);

  const setTab = useCallback(
    (next: CommunityTab) => {
      setTabState(next);
      if (communityId) writeStoredTab(communityId, next);
    },
    [communityId]
  );

  return [tab, setTab] as const;
}
